import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { journal } from "../data";
import { articles } from "../journalContent";
import Seo from "../components/Seo";
import "../journal.css";

const Block = ({ block, id }) => {
  if (block.t === "h2") return <h2 id={id}>{block.c}</h2>;
  if (block.t === "h3") return <h3>{block.c}</h3>;
  if (block.t === "ul") return <ul>{block.c.map((item, i) => <li key={i}>{item}</li>)}</ul>;
  if (block.t === "quote") return <blockquote><p>{block.c}</p></blockquote>;
  return <p>{block.c}</p>;
};

const ShareButtons = ({ entry }) => {
  const [status, setStatus] = useState("");
  const timer = useRef(null);
  const url = typeof window !== "undefined" ? window.location.href.split("#")[0] : "";
  const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(entry.title)}&url=${encodeURIComponent(url)}`;
  useEffect(() => () => clearTimeout(timer.current), []);

  const copyLink = async () => {
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      const field = document.createElement("textarea");
      field.value = url;
      field.style.position = "fixed";
      field.style.opacity = "0";
      const previousFocus = document.activeElement;
      document.body.appendChild(field);
      field.select();
      try { copied = document.execCommand("copy"); } catch { copied = false; }
      field.remove();
      previousFocus?.focus();
    }
    setStatus(copied ? "Link copied." : "Unable to copy. Please copy the address from your browser.");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setStatus(""), 5000);
  };

  return <div className="article-sharing">
    <div className="article-share-actions">
      {entry.xUrl && <a href={entry.xUrl} target="_blank" rel="noopener noreferrer" data-testid="post-original-x">Read on X</a>}
      <a href={shareUrl} target="_blank" rel="noopener noreferrer" data-testid="post-share-x">Share on X</a>
      <button type="button" onClick={copyLink} data-testid="post-copy-link">Copy link</button>
    </div>
    <span className="article-share-status" role="status">{status}</span>
  </div>;
};

const TableOfContents = ({ toc, slug }) => {
  const [activeId, setActiveId] = useState(toc[0]?.id);
  useEffect(() => {
    setActiveId(toc[0]?.id);
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting);
      if (visible.length) setActiveId(visible[0].target.id);
    }, { rootMargin: "-10% 0px -60% 0px", threshold: 0 });
    toc.forEach(item => {
      const heading = document.getElementById(item.id);
      if (heading) observer.observe(heading);
    });
    return () => observer.disconnect();
  }, [slug, toc]);
  return <aside className="article-toc" data-testid="post-toc">
    <p className="article-eyebrow">On this page</p>
    <nav aria-label="Article contents">
      {toc.map(item => <a key={item.id} href={`#${item.id}`} aria-current={activeId === item.id ? "location" : undefined}>{item.title}</a>)}
    </nav>
  </aside>;
};

const JournalPost = () => {
  const { slug } = useParams();
  const entry = journal.find(item => item.slug === slug);
  const blocks = articles[slug];
  const related = journal.filter(item => item.slug !== slug);
  const toc = useMemo(() => (blocks || []).map((block, i) => block.t === "h2" ? { id: `section-${i}`, title: block.c } : null).filter(Boolean), [blocks]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!entry || !blocks) return <Navigate to="/" replace />;

  return <div className="journal-page" data-testid="journal-post-page">
    <Seo title={`${entry.title} · Bunny`} description={entry.desc} image={entry.img} article={entry} />
    <a className="article-skip" href="#article-content">Skip to article</a>
    <div className="article-progress" aria-hidden="true" data-testid="reading-progress-bar" />
    <header className="article-header">
      <Link to="/" className="article-wordmark" data-testid="post-logo">Fredrick Waihenya<span>Co-Founder & CTO, Sereel</span></Link>
      <Link to="/#journal" className="article-back" data-testid="post-back-btn">Back to journal</Link>
    </header>
    <main id="article-content" className="article-layout">
      <article className="article-main">
        <header className="article-intro">
          <p className="article-eyebrow">Journal</p>
          <h1 data-testid="post-title">{entry.title}</h1>
          <p className="article-deck">{entry.desc}</p>
          <div className="article-byline"><span>Bunny</span><span>{entry.date}</span><span>{entry.readTime}</span></div>
        </header>
        <figure className="article-cover"><img src={entry.img} alt="" fetchpriority="high" /></figure>
        <div className="article-body" data-testid="post-body">
          {blocks.map((block, i) => <Block key={i} block={block} id={block.t === "h2" ? `section-${i}` : undefined} />)}
        </div>
        <footer className="article-end">
          <div><p className="article-eyebrow">Written by</p><p className="article-author">Bunny</p><p className="article-author-note">Engineering for institutional tokenization, custody, and digital assets.</p></div>
          <ShareButtons key={slug} entry={entry} />
        </footer>
        <section className="article-related" aria-labelledby="related-heading">
          <p className="article-eyebrow">Continue reading</p>
          <h2 id="related-heading">More from the journal.</h2>
          {related.map((item, i) => <Link to={`/journal/${item.slug}`} className="article-related-link" key={item.slug} data-testid={`related-post-${i}`}>
            <div><span className="article-related-meta">{item.date} · {item.readTime}</span><h3>{item.title}</h3></div>
          </Link>)}
        </section>
      </article>
      {toc.length >= 3 && <TableOfContents toc={toc} slug={slug} />}
    </main>
    <footer className="article-site-footer"><span>© {new Date().getFullYear()} Bunny</span><Link to="/#contact">Get in touch</Link></footer>
  </div>;
};

export default JournalPost;
